import React from 'react';
import styled from 'styled-components';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import { Input as DropdownInput } from '@cobalt/react-dropdown';
import { Text } from '@cobalt/react-typography';

import { SelectorProps } from './index';
import TransIcon from '../TransIcon';

import { SelectOptionType } from '../types';

import SelectStore from '../hooks/useStore';

interface SingleSelectorProps<T extends SelectOptionType = SelectOptionType> extends SelectorProps<T> {
  selectedValue: string;
}

const BaseDropdownInput = styled(DropdownInput)<{ loading?: boolean }>`
  display: flex;
  align-items: center;
`;
const TransparentDropdownInput = styled(BaseDropdownInput)<{ loading?: boolean }>`
  transition-property: border, background;

  &:not([disabled]):not([data-co-disabled]):not(&:focus):not([data-co-focus]):not(&:focus-within):not([data-co-focus-within]):not([class*='_co-input--error']) {
    border-color: transparent;

    &[data-co-hover],
    &:hover {
      background-color: var(--gray-200);
    }

    &:not([data-co-hover]):not(&:hover) {
      .trans-icon-c {
        display: ${(props) => (props.loading ? 'flex' : 'none')};
      }
    }
  }

  &[disabled],
  &[data-co-disabled] {
    border-color: transparent;
    background-color: transparent;
  }
`;

function SingleSelector<T extends SelectOptionType = SelectOptionType>(props: SingleSelectorProps<T>) {
  const {
    isOpen,
    inputRef,
    selectedValue,
    onToggleOpen,
    onClearSelectedValue,

    options,
    placeholder,
    disabled,
    allowClear,
    loading,
    transparent,
    borderless,
    size,
    variation,
    className,
    inputStyle,
    loadingText,
  } = props;

  const theme = useTheme();

  const { cacheRef } = SelectStore.useContainer();

  const selectedOption =
    options?.find((option) => option.value === selectedValue) || cacheRef.current.get(selectedValue);
  const selectedLabel =
    selectedOption?.renderSelectedOption?.() || selectedOption?.renderOption?.() || selectedOption?.label;

  const DropdownInputC = !transparent ? BaseDropdownInput : TransparentDropdownInput;

  return (
    <DropdownInputC
      data-testid="singleSelector"
      forwardedRef={inputRef}
      disabled={disabled}
      loading={loading}
      variation={variation}
      size={size}
      className={className}
      style={inputStyle}
      onClick={() => onToggleOpen()}
    >
      <Flex
        style={{ flex: 1, overflowX: 'hidden' }}
        alignY="center"
        paddingX={borderless ? undefined : 1}
        height="100%"
      >
        {/* Placeholder */}
        {selectedValue.length === 0 && (
          <Text size={size} truncated color={theme.basic500}>
            {!loading ? placeholder : loadingText}
          </Text>
        )}

        {/* Value */}
        {selectedValue.length > 0 && typeof selectedLabel === 'string' && (
          <Text
            data-testid="selectorValue"
            size={size}
            truncated
            title={selectedLabel}
            style={{ pointerEvents: 'auto' }}
          >
            {selectedLabel}
          </Text>
        )}
        {selectedValue.length > 0 && typeof selectedLabel !== 'string' && selectedLabel}
      </Flex>

      {!disabled && (
        <TransIcon
          className="trans-icon-c"
          disabled={disabled!}
          allowClear={allowClear!}
          loading={loading!}
          size={size}
          isOpen={isOpen}
          hasSelected={selectedValue.length > 0}
          onToggleOpen={onToggleOpen}
          onClearSelectedValue={onClearSelectedValue}
        />
      )}
    </DropdownInputC>
  );
}

export default SingleSelector;
